import { useSyncExternalStore } from 'react';

export interface PwaState {
  /** navigator.onLine mirrored through the 'online'/'offline' events. */
  online: boolean;
  /** A new service worker took control of this page: the running code is outdated until a reload. */
  updateReady: boolean;
}

type Listener = () => void;

function initialOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine;
}

let state: PwaState = { online: initialOnline(), updateReady: false };
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of [...listeners]) listener();
}

export const pwaStore = {
  get: (): PwaState => state,
  update: (patch: Partial<PwaState>): void => {
    const next = { ...state, ...patch };
    if (next.online === state.online && next.updateReady === state.updateReady) return;
    state = next;
    emit();
  },
  subscribe: (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Tests only. */
  reset: (): void => {
    state = { online: initialOnline(), updateReady: false };
    emit();
  },
};

export function usePwaState(): PwaState {
  return useSyncExternalStore(pwaStore.subscribe, pwaStore.get, pwaStore.get);
}

/** navigator.onLine + 'online'/'offline' events (task.md §4.14 offline banner). */
export function useOnline(): boolean {
  return usePwaState().online;
}
